import React, { useState } from 'react';
import { RequirementsProps } from '../../../tools/context';
import { Button } from '../../Button';
import { Picto } from '../../Picto';
import CategoryPopup, { CategoryPopupData, FiltersConfigurationType } from './categoryPopup';

export interface SearchBarProps {
  requirements?: RequirementsProps;
  heroSearch?: boolean;
  mapOpened?: boolean;
  mapGridCallback?: (p: boolean) => undefined | void;
  handleInput: (p: string) => void;
  categData: CategoryPopupData;
  filtersConfiguration: FiltersConfigurationType;
  categoryCallback: (p: CategoryPopupData) => void | undefined;
  /** Masque le bouton « Filtrer les catégories » (ex. grille alimentée par Apidae) */
  hideCategoryFilter?: boolean;
  /** Remplace le texte « Go » par une flèche (utile pour certains écrans, ex. Apidae). */
  goAsCaret?: boolean;
  /** Affiche le bouton “Dates” (filtre agenda). */
  showDateFilter?: boolean;
  /** État contrôlé du filtre dates (ISO yyyy-mm-dd). */
  dateFilter?: { from?: string | null; to?: string | null };
  /** Callback quand le filtre dates change. */
  onDateFilterChange?: (next: { from?: string | null; to?: string | null }) => void;
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Date locale yyyy-mm-dd (évite le décalage UTC de toISOString). */
const toIsoLocal = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const atLocalMidnight = (d = new Date()): Date => {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const addLocalDays = (d: Date, days: number): Date => {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + days);
  return copy;
};

/** Samedi → dimanche (week-end en cours ou prochain). */
const getThisWeekendRange = (ref = new Date()): { from: string; to: string } => {
  const today = atLocalMidnight(ref);
  const day = today.getDay();

  if (day === 0) {
    const sun = today;
    const sat = addLocalDays(today, -1);
    return { from: toIsoLocal(sat), to: toIsoLocal(sun) };
  }
  if (day === 6) {
    const sat = today;
    const sun = addLocalDays(today, 1);
    return { from: toIsoLocal(sat), to: toIsoLocal(sun) };
  }

  const daysUntilSat = 6 - day;
  const sat = addLocalDays(today, daysUntilSat);
  const sun = addLocalDays(sat, 1);
  return { from: toIsoLocal(sat), to: toIsoLocal(sun) };
};

/** Aujourd’hui → aujourd’hui + N jours (dates locales). */
const getRangeFromToday = (daysAhead: number): { from: string; to: string } => {
  const start = atLocalMidnight();
  const end = addLocalDays(start, daysAhead);
  return { from: toIsoLocal(start), to: toIsoLocal(end) };
};

export const SearchBar = (props: SearchBarProps) => {
  const { requirements, heroSearch, mapGridCallback } = props;
  const [showCategory, setShowCategory] = useState<boolean>(false);
  const [showMap, setShowMap] = useState<boolean>(props.mapOpened || false);
  const [showDatePopover, setShowDatePopover] = useState<boolean>(false);
  const [localFrom, setLocalFrom] = useState<string>(props.dateFilter?.from || '');
  const [localTo, setLocalTo] = useState<string>(props.dateFilter?.to || '');

  const handleMapGridCallback = () => {
    mapGridCallback(!showMap);

    setShowMap((prev) => !prev);
  };

  const handleGO = () => {};

  const hasDateFilter = !!(props.dateFilter?.from || props.dateFilter?.to);
  const dateLabel = hasDateFilter
    ? props.dateFilter?.from && props.dateFilter?.to
      ? `${props.dateFilter.from} → ${props.dateFilter.to}`
      : props.dateFilter?.from
        ? `Dès ${props.dateFilter.from}`
        : props.dateFilter?.to
          ? `Jusqu’au ${props.dateFilter.to}`
          : 'Dates'
    : 'Dates';

  const applyDateFilter = (next: { from?: string | null; to?: string | null }) => {
    props.onDateFilterChange?.(next);
  };

  const onApplyCustomRange = () => {
    const from = localFrom && ISO_DATE_RE.test(localFrom) ? localFrom : null;
    const to = localTo && ISO_DATE_RE.test(localTo) ? localTo : null;
    applyDateFilter({ from, to });
    setShowDatePopover(false);
  };

  const onClearDates = () => {
    setLocalFrom('');
    setLocalTo('');
    applyDateFilter({ from: null, to: null });
    setShowDatePopover(false);
  };

  const setPreset = (range: { from: string; to: string }) => {
    setLocalFrom(range.from);
    setLocalTo(range.to);
    applyDateFilter(range);
    setShowDatePopover(false);
  };

  const presetToday = () => {
    const { from } = getRangeFromToday(0);
    setPreset({ from, to: from });
  };

  const presetThisWeekend = () => {
    setPreset(getThisWeekendRange());
  };

  const presetThisWeek = () => {
    setPreset(getRangeFromToday(7));
  };

  const presetNext30Days = () => {
    setPreset(getRangeFromToday(30));
  };

  return (
    <div className={`lightSearchBar`}>
      <div className="inputBlock">
        <input
          onChange={(e) => props.handleInput(e.target.value)}
          placeholder={'Rechercher par nom'}
        />
        <Picto iconKey={'target'} />
      </div>
      <Button className="validate" theme={'yellow'} onClick={handleGO}>
        {heroSearch ? 'Valider' : props.goAsCaret ? <Picto iconKey={'arrow-right'} /> : 'Go'}
      </Button>
      <div className="filters">
        {!heroSearch && !props.hideCategoryFilter && (
          <Button
            className="categoryButton"
            theme={'transparent'}
            onClick={() => setShowCategory(true)}
          >
            Filtrer les catégories <Picto iconKey={'cursors'} />
          </Button>
        )}
        {props.showDateFilter && props.onDateFilterChange && (
          <div className="dateFilter">
            <Button
              className={`dateButton ${hasDateFilter ? 'isActive' : ''}`}
              theme={'transparent'}
              onClick={() => setShowDatePopover((v) => !v)}
            >
              {dateLabel} <Picto iconKey={'calendar'} />
            </Button>
            {showDatePopover && (
              <div className="datePopover" role="dialog" aria-label="Filtrer par date">
                <div className="datePopover__presets">
                  <button type="button" onClick={presetToday}>
                    Aujourd’hui
                  </button>
                  <button type="button" onClick={presetThisWeekend}>
                    Ce week-end
                  </button>
                  <button type="button" onClick={presetThisWeek}>
                    Cette semaine
                  </button>
                  <button type="button" onClick={presetNext30Days}>
                    Prochains 30 jours
                  </button>
                </div>

                <div className="datePopover__range">
                  <label>
                    Du
                    <input
                      type="date"
                      value={localFrom}
                      onChange={(e) => setLocalFrom(e.target.value)}
                    />
                  </label>
                  <label>
                    Au
                    <input
                      type="date"
                      value={localTo}
                      onChange={(e) => setLocalTo(e.target.value)}
                    />
                  </label>
                </div>

                <div className="datePopover__actions">
                  <button type="button" className="clear" onClick={onClearDates}>
                    Effacer
                  </button>
                  <button type="button" className="apply" onClick={onApplyCustomRange}>
                    Appliquer
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {mapGridCallback && (
          <Button className="mapButton" theme={'transparent'} onClick={handleMapGridCallback}>
            {showMap ? (
              <>
                Liste
                <Picto iconKey={'list'} />
              </>
            ) : (
              <>
                Carte
                <Picto iconKey={'place'} />
              </>
            )}
          </Button>
        )}
      </div>
      {showCategory && (
        <CategoryPopup
          requirements={requirements}
          closeCallback={() => setShowCategory(false)}
          callback={props.categoryCallback}
          filtersConfiguration={props.filtersConfiguration}
          data={props.categData}
        />
      )}
    </div>
  );
};
